import { FullScreenLoading, ServerTable } from '@civilgrid/ui/components';
import { buildSearchPayload, useTableStoreState } from '@civilgrid/ui/hooks';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { listProjectUtilityChecks, type ProjectUtilityCheck } from '~/api/civilsearch';
import { formatRowLabel } from '~/common/projectUtils';
import CivilSearchLayout from '../common/CivilSearchLayout';
import { useColumns } from './columns';
import DataRequestsTable from './DataRequestsTable';
import SSWEGCompletionForm from './SSWEGCompletionForm';

const getProjectUtilityCheckRowLabel = (row: ProjectUtilityCheck) =>
  formatRowLabel('project', row.projectName, [row.projectShortId, row.organizationName].filter(Boolean).join(', '));

export default function ProjectUtilityChecks() {
  const enabled = useFeatureFlagEnabled('ssweg-enabled');
  const columns = useColumns();
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);

  const stateControl = useTableStoreState({
    defaultPageSize: 100,
    initialSorting: [{ id: 'completionPercentage', desc: true }],
  });

  const { pagination, sorting, filters, globalFilter } = stateControl;

  const query = useQuery({
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['project-utility-checks', { pagination, sorting, filters, globalFilter }],
    queryFn: async () => {
      const searchPayload = buildSearchPayload({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        filters,
        globalFilter,
      });
      const apiPayload = {
        ...searchPayload,
        sort: searchPayload.sort?.[0],
      };
      return listProjectUtilityChecks({ search: apiPayload });
    },
    enabled: enabled === true,
  });

  const projects = query.data?.projects ?? [];

  // Derive selectedProject from query data so it stays fresh after refetches
  const selectedProject = useMemo(
    () => projects.find((p) => p.projectId === selectedProjectId),
    [projects, selectedProjectId],
  );

  // Clear selection when table params change
  useEffect(() => {
    setSelectedProjectId(undefined);
  }, []);

  if (enabled === undefined) return <FullScreenLoading />;
  if (!enabled) return <Navigate to="/admin/civil-search" />;

  if (query.isError) {
    return <div>Failed to fetch SSWEG data.</div>;
  }

  return (
    <CivilSearchLayout title="SSWEG">
      <div className="grid h-[calc(100vh-6rem)] grid-rows-[4fr_1fr] gap-4">
        <div className="min-h-80 grid grid-cols-2 gap-4">
          <ServerTable
            id="project-utility-checks"
            aria-label="SSWEG projects"
            columns={columns}
            data={{
              data: projects,
              rowCount: query.data?.totalDocuments ?? 0,
              isLoading: query.isLoading,
              isFetching: query.isFetching,
              error: query.error || null,
            }}
            stateControl={stateControl}
            defaultColumn={{
              minSize: 60,
            }}
            enableColumnGrouping={false}
            isLoading={query.isLoading}
            enableRowSelection={true}
            enableMultiRowSelection={false}
            enableRowClickSelection={true}
            getRowId={(row) => row.projectId}
            getRowLabel={getProjectUtilityCheckRowLabel}
            className="min-h-0"
            onSelectionChange={(selection) => {
              if (selection.mode === 'partial' && selection.selectedIds.length === 1) {
                setSelectedProjectId(selection.selectedIds[0]);
              } else {
                setSelectedProjectId(undefined);
              }
            }}
          />
          <div id="project-utility-check-details" className="min-h-0 overflow-auto">
            {selectedProject ? (
              <ProjectUtilityCheckDetails project={selectedProject} />
            ) : (
              <div className="flex h-32 items-center justify-center text-gray-400">
                Select a project to view details.
              </div>
            )}
          </div>
        </div>
        <div className="rounded-md border">
          {selectedProject ? (
            <SSWEGCompletionForm project={selectedProject} />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400">
              Select a project to set SSWEG check states.
            </div>
          )}
        </div>
      </div>
    </CivilSearchLayout>
  );
}

function ProjectUtilityCheckDetails({ project }: { project: ProjectUtilityCheck }) {
  return <DataRequestsTable projectId={project.projectId} />;
}
